<div class="h-screen flex flex-col bg-gray-950 text-white p-6 gap-6">
  <header class="flex items-center gap-3 shrink-0">
    <h1 class="flex gap-3 items-end text-sm font-semibold tracking-wide uppercase text-gray-400">
      <mai-mib-logo theme="dark"></mai-mib-logo>
      Ingest — Live Case
    </h1>
    <mib-badge mode="word" [theme]="connectionBadgeTheme()">{{ connectionLabel() }}</mib-badge>
  </header>

  <div
    class="relative flex-1 min-h-0 bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center"
  >
    <video
      #previewVideo
      autoplay
      playsinline
      muted
      aria-label="Local camera preview"
      class="w-full h-full object-contain"
    ></video>

    @if (!hasActiveVideo()) {
      <p class="absolute text-gray-500 text-sm" aria-live="polite">Camera not active</p>
    }
  </div>

  <footer class="flex max-2xl:flex-col items-center 2xl:justify-between shrink-0">
    <div
      class="flex items-center gap-6 max-2xl:self-start"
      role="status"
      aria-label="Stream status"
    >
      <div class="flex items-center gap-2">
        <svg mib-video-icon class="h-4 text-gray-400" aria-hidden="true"></svg>
        <mib-badge
          mode="dot"
          size="large"
          [theme]="hasActiveVideo() ? 'success' : 'danger'"
        ></mib-badge>
        <select
          aria-label="Select camera"
          class="bg-gray-800 text-sm text-gray-300 border border-gray-700 rounded px-2 py-1 outline-none focus:border-gray-500"
          (change)="switchVideoDevice($any($event.target).value)"
        >
          @for (device of videoDevices(); track device.deviceId) {
            <option
              [value]="device.deviceId"
              [selected]="device.deviceId === selectedVideoDeviceId()"
            >
              {{ device.label || 'Camera ' + ($index + 1) }}
            </option>
          }
        </select>
      </div>
      <div class="flex items-center gap-2">
        <svg mib-microphone-icon class="h-4 text-gray-400" aria-hidden="true"></svg>
        <mib-badge
          mode="dot"
          size="large"
          [theme]="hasActiveAudio() ? 'success' : 'danger'"
        ></mib-badge>
        <select
          aria-label="Select microphone"
          class="bg-gray-800 text-sm text-gray-300 border border-gray-700 rounded px-2 py-1 outline-none focus:border-gray-500"
          (change)="switchAudioDevice($any($event.target).value)"
        >
          @for (device of audioDevices(); track device.deviceId) {
            <option
              [value]="device.deviceId"
              [selected]="device.deviceId === selectedAudioDeviceId()"
            >
              {{ device.label || 'Microphone ' + ($index + 1) }}
            </option>
          }
        </select>
      </div>
      <div class="flex items-center gap-2">
        <app-audio-level-indicator [track]="activeAudioTrack()"></app-audio-level-indicator>
      </div>
    </div>

    @if (isConnected()) {
      <div class="flex items-center gap-4 max-2xl:self-end">
        <div class="flex items-center gap-2">
          <input id="voiceLive" type="checkbox" [formControl]="voiceOutputEnabled" />
          <label for="voiceLive" class="text-sm text-gray-300">Voice</label>
        </div>
        <div class="flex items-center gap-2">
          <label for="maxWords" class="text-sm text-gray-300">Max words</label>
          <input
            id="maxWords"
            type="number"
            min="10"
            max="500"
            [formControl]="maxWords"
            class="w-20 bg-gray-800 text-sm text-gray-300 border border-gray-700 rounded px-2 py-1 outline-none focus:border-gray-500 text-center"
          />
        </div>
        <mib-button
          darkMode
          theme="secondary"
          [attr.aria-pressed]="ingestActive()"
          [attr.aria-label]="ingestActive() ? 'Stop streaming' : 'Start streaming'"
          (click)="toggleIngest()"
        >
          @if (ingestActive()) {
            <svg mib-pause-icon aria-hidden="true"></svg>
            Pause ingestion
          } @else {
            <svg mib-play-icon aria-hidden="true"></svg>
            Start ingestion
          }
        </mib-button>
        <mib-button theme="danger" mode="outlined" darkMode (click)="terminateSession()"
          >Terminate session</mib-button
        >
      </div>
      <!-- TODO: start session button requires to have an independant websocket managed by the backend,
        we'll do that later. -->
    } @else if (isDisconnected() && !manuallyTerminatedSession()) {
      <div class="flex items-center gap-4 max-2xl:self-end">
        <div class="flex items-center gap-2">
          <input id="voice" type="checkbox" [formControl]="voiceOutputEnabled" />
          <label for="voice" class="text-sm text-gray-300">Enable voice responses</label>
        </div>
        <div class="flex items-center gap-2">
          <label for="maxWordsPre" class="text-sm text-gray-300">Max words</label>
          <input
            id="maxWordsPre"
            type="number"
            min="10"
            max="500"
            [formControl]="maxWords"
            class="w-20 bg-gray-800 text-sm text-gray-300 border border-gray-700 rounded px-2 py-1 outline-none focus:border-gray-500 text-center"
          />
        </div>
        <mib-button
          theme="primary"
          darkMode
          [disabled]="!mediaPermissionGranted()"
          (click)="startSession()"
          >Start session</mib-button
        >
      </div>
      @if (mediaPermissionError()) {
        <p class="text-red-400 text-sm">{{ mediaPermissionError() }}</p>
      }
    }
  </footer>
</div>
